/* eslint-disable no-confusing-arrow */
import React, { useState, useEffect, useContext } from 'react';
import queryString from 'query-string';
import { Route, Redirect, useLocation } from 'react-router-dom';
import Loading from '../Loading';
import { getAccessToken, isTokenUsable } from '../../utils/accessToken';
import { refreshSession, REFRESH_SESSION_ENDED } from '../../utils/session';
import { ActiveFarmContext } from '../../contexts/ActiveFarm';
import { FarmsContext } from '../../contexts/Farms';
import { useCustomAlert } from '../CustomAlert';


const AuthRoute = props => {

    const prevLocation = useLocation();
    const customAlert = useCustomAlert();
    const { setActiveFarm } = useContext(ActiveFarmContext);
    const { farms } = useContext(FarmsContext);
    const [authStatus, setAuthStatus] = useState('loading'); // loading, auth, not-auth
    const [isFarmLoading, setIsFarmLoading] = useState(true);
    const [activeFarmFromQueryString, setActiveFarmFromQueryString] = useState('');

    useEffect(() => {
        checkAuth();
    }, []);

    /**
     * Decides whether the app may render, which is now a question about the session
     * rather than about the access token.
     *
     * An expired access token used to mean "log in again", because tokens lasted a
     * year and one that had lapsed really was the end. At 15 minutes it is the
     * ordinary state of any returning user: the session lives in the refresh cookie,
     * which outlives it by 30 days, so the expired token is a prompt to renew.
     */
    const checkAuth = async () => {
        const theToken = getAccessToken();

        if (isTokenUsable(theToken)) {
            setAuthStatus('auth');
            return;
        }

        // Offline is the normal case for this app, and /auth/refresh is unreachable
        // from a field with no signal. A stored token — expired or not — is taken as
        // evidence of a session and the app opens on its offline data; the first
        // request made once there is signal renews or ends the session for real.
        // Nothing is conceded by this: the API rejects the expired token regardless,
        // so what it unlocks is the local IndexedDB copy the app already trusts.
        if (!window.navigator.onLine) {
            setAuthStatus(theToken ? 'auth' : 'not-auth');
            return;
        }

        const { status, token } = await refreshSession();

        if (token) {
            setAuthStatus('auth');
        }
        else if (status === REFRESH_SESSION_ENDED) {
            // The one answer that actually means the session is over.
            setAuthStatus('not-auth');
        }
        else {
            // The refresh never got an answer, or the API answered with a 5xx. An API
            // having a bad minute must not sign every user out, so this falls back to
            // the same offline grace as above.
            setAuthStatus(theToken ? 'auth' : 'not-auth');
        }
    }

    const getActiveFarmFromQueryString = () => {
        const farmFromQueryString = queryString.parse(props.location?.search)?.farmId;
        if (farmFromQueryString) {
            setActiveFarmFromQueryString(farmFromQueryString);
        }
        else {
            setIsFarmLoading(false);
        }
    }

    useEffect(() => {
        if (activeFarmFromQueryString && farms?.length) {
            const farm = farms.find(i => +i.id === +activeFarmFromQueryString);
            if (farm) {
                (async () => {
                    await setActiveFarm(farm.id, farm.timeZone);
                    setIsFarmLoading(false);
                })();
            }
            else {
                customAlert.error({
                    headlineIcon: 'zelpicon-issue',
                    headlineIconColor: 'red',
                    headline: 'Farm not found!',
                    content: "The requested farm not found or you don't have access to that farm yet.",
                    buttonText: 'Ok',
                });
            }
            setIsFarmLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFarmFromQueryString, farms]);

    useEffect(() => {
        if (authStatus === 'auth') {
            getActiveFarmFromQueryString();
        }
        else {
            setIsFarmLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStatus]);


    if (authStatus !== 'loading' && !isFarmLoading) {
        const { component: Component, ...rest } = props;
        return <Route
            {...rest}
            render={componentProps => (authStatus === 'auth')
                ? <Component {...componentProps} />
                : <Redirect to={`${((prevLocation?.pathname && prevLocation?.pathname?.length > 1) ||
                    prevLocation?.search)
                    ? '/login?redirectTo=' + prevLocation?.pathname + prevLocation?.search + prevLocation?.hash
                    : (queryString.parse(props.location?.search)?.farmId ? '/login?farmId=' +
                        queryString.parse(props.location?.search)?.farmId : '/login')}`} />} />
    }
    return (
        <Loading isFullPage />
    )
}

export default AuthRoute;
